import type {
  BreakerDeviceResponse,
  BreakerSelectivityResponse,
} from "../services/breakerSelectivity";
import type { BreakerSelectivityWarningCode } from "../services/breakerSelectivityContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  STATUS_LABELS as UPS_STATUS_LABELS,
  STATUS_TONE as UPS_STATUS_TONE,
} from "./UPSResultSummary";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The same words and the same colour as the Transformer, DG, UPS, PV and starter
// summaries for a calculated, a warned and a review-required study: missing evidence
// is a review the engineer owes, not a design failure. A failed check is a failure.
// The maps are the UPS summary's, typed here against the breaker selectivity status
// so a value only this module gains cannot pass without words and a colour; the
// NO_SOLUTION words are this module's own, because "no standard rating fits" is not
// what a failed duty or selectivity check means.
const STATUS_LABELS: Record<BreakerSelectivityResponse["status"], string> = {
  ...UPS_STATUS_LABELS,
  NO_SOLUTION: "Duty or selectivity not met",
};

const STATUS_TONE: Record<BreakerSelectivityResponse["status"], "pass" | "fail" | "warning"> =
  UPS_STATUS_TONE;

// A duty check that failed: the device as rated cannot be accepted (A21 (b)).
const DUTY_NOT_MET_CODES = [
  "ICU_BELOW_PROSPECTIVE_CURRENT",
  "ICS_BELOW_PROSPECTIVE_CURRENT",
  "ICM_BELOW_PEAK_CURRENT",
] as const satisfies readonly BreakerSelectivityWarningCode[];

// A duty check the engineer still owes: declared cascading (never a pass), no peak for
// the making check, or a rated voltage below the system voltage (IR-001 F1).
const DUTY_REVIEW_CODES = [
  "RELIES_ON_CASCADING",
  "MAKING_CHECK_NOT_EVALUATED",
  "RATED_VOLTAGE_BELOW_SYSTEM",
] as const satisfies readonly BreakerSelectivityWarningCode[];

const SELECTIVITY_NOT_MET: BreakerSelectivityWarningCode = "SELECTIVITY_NOT_MET";
const SELECTIVITY_REVIEW: BreakerSelectivityWarningCode = "SELECTIVITY_EVIDENCE_NOT_ESTABLISHED";
const SELECTIVITY_PARTIAL_NOT_REQUIRED: BreakerSelectivityWarningCode =
  "SELECTIVITY_PARTIAL_NOT_REQUIRED";

export type DutyKind = "adequate" | "not-adequate" | "review";

function hasCode(device: BreakerDeviceResponse, codes: readonly string[]): boolean {
  return device.warnings.some((warning) => codes.includes(warning.code));
}

/**
 * How one device's duty checks fared (Icu, Ics, Icm, Ue), apart from selectivity: a failed
 * check is not adequate; cascading reliance, a making check not made or a low rated
 * voltage is a review; otherwise the device is adequate for its duty.
 */
export function dutyKind(device: BreakerDeviceResponse): DutyKind {
  if (hasCode(device, DUTY_NOT_MET_CODES)) {
    return "not-adequate";
  }
  if (hasCode(device, DUTY_REVIEW_CODES)) {
    return "review";
  }
  return "adequate";
}

/** The incomer and every outgoing device, in the order the result holds them. */
export function devicesOf(result: BreakerSelectivityResponse): BreakerDeviceResponse[] {
  return [result.incomer, ...result.outgoing];
}

/** "Duty or selectivity not met for 1 breaker" / "... for 3 breakers"; the other statuses' own words. */
export function statusHeading(result: BreakerSelectivityResponse): string {
  if (result.status !== "NO_SOLUTION") {
    return STATUS_LABELS[result.status];
  }
  const notMet = devicesOf(result).filter((device) => device.status === "NO_SOLUTION").length;
  return `${STATUS_LABELS.NO_SOLUTION} for ${notMet} ${notMet === 1 ? "breaker" : "breakers"}`;
}

/**
 * The device with the smallest margin of the given kind, or null when no device carries
 * one. The margins are the engine's exact strings; they are only compared here, never
 * recomputed, and a tie keeps the first device.
 */
export function lowestMargin(
  devices: readonly BreakerDeviceResponse[],
  margin: "icu_margin_ka" | "icm_margin_ka",
): BreakerDeviceResponse | null {
  let lowest: BreakerDeviceResponse | null = null;
  for (const device of devices) {
    const value = device[margin];
    if (value === null) {
      continue;
    }
    if (lowest === null || Number(value) < Number(lowest[margin])) {
      lowest = device;
    }
  }
  return lowest;
}

type BreakerSelectivityResultSummaryProps = {
  result: BreakerSelectivityResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): how many breakers were
// held against the fault level and how their duty fared, the Ik'' and ip they were
// held against, how the outgoing pairs fared on selectivity, the tightest breaking
// and making margins, and who relies on cascading. No figure is calculated here: the
// margins are the engine's, and the tiles only count device codes and statuses and
// pick an existing value. Display rule A12: four significant figures on screen, the
// exact engine decimal in the tooltip (title attribute).
export function BreakerSelectivityResultSummary({ result }: BreakerSelectivityResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const devices = devicesOf(result);

  const kinds = devices.map(dutyKind);
  const adequate = kinds.filter((kind) => kind === "adequate").length;
  const notAdequate = kinds.filter((kind) => kind === "not-adequate").length;
  const dutyReview = kinds.filter((kind) => kind === "review").length;

  const pairs = result.outgoing;
  const selective = pairs.filter((device) => device.selective === true).length;
  const selectivityNotMet = pairs.filter((device) => hasCode(device, [SELECTIVITY_NOT_MET])).length;
  const selectivityReview = pairs.filter((device) => hasCode(device, [SELECTIVITY_REVIEW])).length;
  const partialNotRequired = pairs.filter((device) =>
    hasCode(device, [SELECTIVITY_PARTIAL_NOT_REQUIRED]),
  ).length;

  const cascading = devices.filter((device) => device.relies_on_cascading === true);
  const warningCount =
    result.warnings.length +
    devices.reduce((count, device) => count + device.warnings.length, 0);

  const faultLevel = formatQuantity(result.prospective_current_ka, "kA");
  const peak = formatQuantity(result.peak_current_ka, "kA");
  const lowestBreaking = lowestMargin(devices, "icu_margin_ka");
  const breakingMargin = formatQuantity(lowestBreaking?.icu_margin_ka ?? null, "kA");
  const lowestMaking = lowestMargin(devices, "icm_margin_ka");
  const makingMargin = formatQuantity(lowestMaking?.icm_margin_ka ?? null, "kA");

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} ·{" "}
          {result.jurisdiction_profile} profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Breakers</dt>
          {/* Counts of device duty codes, not engineering quantities. */}
          <dd data-summary-field="breakers" data-summary-value="text">
            {devices.length} checked · {adequate} adequate · {notAdequate} not adequate ·{" "}
            {dutyReview} review
          </dd>
        </div>
        <div>
          <dt>Fault level</dt>
          <dd data-summary-field="fault-level" title={faultLevel.exact ?? undefined}>
            {faultLevel.display} at {result.fault_bus_code}
          </dd>
        </div>
        <div>
          <dt>Peak current</dt>
          {/* ip as the fault level holds it; empty when it was not held, so the making
              check was not made. */}
          <dd data-summary-field="peak-current" title={peak.exact ?? undefined}>
            {peak.display}
          </dd>
        </div>
        <div>
          <dt>Selectivity</dt>
          <dd data-summary-field="selectivity" data-summary-value="text">
            {pairs.length} {pairs.length === 1 ? "pair" : "pairs"} · {selective} selective ·{" "}
            {selectivityNotMet} not met · {selectivityReview} review
            {partialNotRequired > 0 ? ` · ${partialNotRequired} partial (not required)` : ""}
          </dd>
        </div>
        <div>
          <dt>Lowest breaking margin</dt>
          {/* Icu - Ik'' of the tightest device, as the engine reported it; negative when
              that device's check failed or relies on cascading. */}
          <dd data-summary-field="lowest-breaking-margin" title={breakingMargin.exact ?? undefined}>
            {lowestBreaking === null
              ? EMPTY_QUANTITY
              : `${breakingMargin.display} (${lowestBreaking.code})`}
          </dd>
        </div>
        <div>
          <dt>Lowest making margin</dt>
          {/* Icm - ip of the tightest device; empty when no device was held against a peak. */}
          <dd data-summary-field="lowest-making-margin" title={makingMargin.exact ?? undefined}>
            {lowestMaking === null ? EMPTY_QUANTITY : `${makingMargin.display} (${lowestMaking.code})`}
          </dd>
        </div>
        {cascading.length > 0 ? (
          <div>
            <dt>Relies on cascading</dt>
            <dd data-summary-field="relies-on-cascading" data-summary-value="text">
              {cascading.length} {cascading.length === 1 ? "breaker" : "breakers"} (
              {cascading.map((device) => device.code).join(", ")})
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { DUTY_NOT_MET_CODES, DUTY_REVIEW_CODES, STATUS_LABELS, STATUS_TONE };
